import { memo, useMemo } from 'react';

/**
 * Rendu STATIQUE d'une horloge « placer_aiguilles » (gen_1032).
 *
 * Même astuce que ProgrammeConstructionRenderer : le composant dessine le jeu
 * d'aiguilles qu'on lui passe dans `reponse` — la SOLUTION (bonne_reponse
 * sémantique { aiguilles: {h, min, sec} }) ou la RÉPONSE DE L'ÉLÈVE (angles
 * posés { heures, minutes, secondes }). Les deux formats sont acceptés.
 *
 * @param {string|Object} reponse - JSON (ou objet) : solution ou angles élève
 * @param {boolean} avecTrotteuse - dessine la trotteuse même à 0
 * @param {boolean} light - version compacte (correction élève)
 * @param {boolean} vierge - cadran SANS aiguilles (impression : à compléter)
 * @param {string|number} largeur - largeur CSS imposée (correction sur place : « 100% »,
 *   pour faire la taille du cadran de l'élève posé à côté) ; sinon 200, ou 150 en light
 */

const CX = 120;
const CY = 120;
const BLEU = '#1E3A8A';

const pointe = (angleDeg, r) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) };
};

const AIGUILLES = {
    heures: { points: '115.5,122 120,62 124.5,122 120,134', fill: BLEU },
    minutes: { points: '117.2,122 120,32 122.8,122 120,134', fill: BLEU },
    secondes: { points: '119,124 120,26 121,124 120,142', fill: '#dc2626' },
};

/** JSON/objet (solution sémantique ou angles élève) → angles par aiguille. */
export const anglesDepuisReponse = (reponse) => {
    let obj = reponse;
    if (typeof obj === 'string') { try { obj = JSON.parse(obj); } catch { return null; } }
    if (!obj || typeof obj !== 'object') return null;
    if (obj.aiguilles) {
        const { h, min, sec } = obj.aiguilles;
        if (typeof h !== 'number' || typeof min !== 'number') return null;
        return {
            heures: (h % 12) * 30 + min * 0.5,
            minutes: min * 6,
            ...(sec != null ? { secondes: sec * 6 } : {}),
        };
    }
    if (typeof obj.heures === 'number' && typeof obj.minutes === 'number') {
        return {
            heures: obj.heures,
            minutes: obj.minutes,
            ...(typeof obj.secondes === 'number' ? { secondes: obj.secondes } : {}),
        };
    }
    return null;
};

const HorlogeAiguillesRenderer = memo(function HorlogeAiguillesRenderer({
    reponse, avecTrotteuse = false, light = false, vierge = false, largeur = null,
}) {
    const angles = useMemo(() => (vierge ? null : anglesDepuisReponse(reponse)), [reponse, vierge]);

    const graduations = useMemo(() => {
        const items = [];
        for (let i = 0; i < 60; i++) {
            const maj = i % 5 === 0;
            const p1 = pointe(i * 6, maj ? 96 : 101);
            const p2 = pointe(i * 6, 106);
            items.push(
                <line key={`g${i}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke={maj ? '#64748b' : '#94a3b8'} strokeWidth={maj ? 2.5 : 1} />,
            );
        }
        for (let i = 1; i <= 12; i++) {
            const p = pointe(i * 30, 80);
            items.push(
                <text key={`n${i}`} x={p.x} y={p.y + 6} fontSize={17} fontWeight={700}
                    fill={BLEU} textAnchor="middle" fontFamily="system-ui, sans-serif">{i}</text>,
            );
        }
        return items;
    }, []);

    const mains = angles
        ? ['heures', 'minutes', ...((angles.secondes !== undefined || avecTrotteuse) ? ['secondes'] : [])]
        : [];

    return (
        <svg
            viewBox="0 0 240 240"
            className="block"
            style={{ width: largeur ?? (light ? 150 : 200), height: 'auto', maxWidth: '100%' }}
            role="img"
            aria-label={vierge ? 'Cadran d\'horloge vierge, aiguilles à dessiner.' : 'Cadran d\'horloge avec les aiguilles placées.'}
        >
            <circle cx={CX} cy={CY} r={112} fill="#eff6ff" stroke={BLEU} strokeWidth={4} />
            {graduations}
            {mains.map((m) => (
                <polygon key={m} points={AIGUILLES[m].points} fill={AIGUILLES[m].fill}
                    transform={`rotate(${angles[m] ?? 0} ${CX} ${CY})`} />
            ))}
            <circle cx={CX} cy={CY} r={5.5} fill={BLEU} />
        </svg>
    );
});

export default HorlogeAiguillesRenderer;
